import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, InputNumber, Radio, Select, Space, message } from 'antd';
import {
  NONSTD_BILLING_UNITS,
  NONSTD_RULE_LEVEL_TEXT,
  NonStdApplicableRule,
  NonStdChargeLine,
  NonStdChargeLinePayload,
  PriceHint,
} from '../../types/nonstandardOps';

export const UNIT_TEXT: Record<string, string> = {
  PER_ITEM: '件', PER_BOX: '箱', PER_PALLET: '托盘', PER_LB: '磅', PER_KG: '千克', PER_CUFT: '立方英尺',
  PER_CBM: '立方米', PER_CC: '立方厘米', PER_VALUE: '价值', PER_ORDER: '订单', PER_MINUTE: '分钟',
};
const LEVEL_ORDER = ['project', 'customer', 'system'];

export const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

/** 数量后面的单位：规则的展示单位常写成“每件”“每标签”，这里是“数量 + 单位”，去掉开头的“每”，显示成“100 件” */
export const plainUnit = (text?: string | null) => (text || '').trim().replace(/^每\s*/, '');

/** 旧版本创建的收费行（没有引用规则、也不是临时收费）：只能改说明和是否对客户可见 */
export const isLegacyLine = (line?: NonStdChargeLine) => !!line && !line.is_temporary && !line.rule_id;

type Mode = 'rule' | 'temporary';

/** 梯度规则里 value 所在档位的单价（下限含、上限不含）；没有命中返回 null */
const tierPriceOf = (rule: NonStdApplicableRule | null, value: number | null): number | null => {
  if (!rule || value === null) return null;
  const tier = rule.tiers.find(t => (t.min_value === null || value >= Number(t.min_value)) && (t.max_value === null || value < Number(t.max_value)));
  return tier ? Number(tier.unit_price) : null;
};

export interface ChargeFormProps {
  /** 按所选规则、计费数量（和梯度规则的档位依据值）查单价 */
  fetchHint: (params: { rule_id: number; quantity: number; tier_value?: number }) => Promise<PriceHint>;
  /** 适用的收费规则；null 表示还在加载 */
  rules: NonStdApplicableRule[] | null;
  initial?: NonStdChargeLine;
  /** 新增时提交按钮的文字，默认“添加收费行” */
  submitText?: string;
  onSubmit: (payload: NonStdChargeLinePayload) => Promise<void>;
  onCancel?: () => void;
}

/**
 * 新增/修改收费（仅管理员）：库存非标准操作指令终审的收费行、管理员手动创建收费记录共用。
 * 和标准指令的计费一样，先确定依据哪条收费规则，再确定单价和数量：
 * - 选择已有收费规则（系统级/客户级/项目级）：单价按规则带出，可以改价但必须写原因；
 * - 临时收费：没有可用的规则时，自己填名称、计费单位、单价和原因（不会在收费规则里落库）。
 */
export const ChargeForm: React.FC<ChargeFormProps> = ({ fetchHint, rules, initial, submitText, onSubmit, onCancel }) => {
  const legacy = isLegacyLine(initial);
  const [mode, setMode] = useState<Mode>(initial ? (initial.is_temporary ? 'temporary' : 'rule') : 'rule');
  const [ruleId, setRuleId] = useState<number | undefined>(initial?.rule_id || undefined);
  const [description, setDescription] = useState(initial?.description || '');
  const [quantity, setQuantity] = useState<number | null>(initial ? Number(initial.quantity) : null);
  // 引用规则：null=按规则价；临时收费：必填
  const [unitPrice, setUnitPrice] = useState<number | null>(
    initial && (initial.price_source === 'override' || initial.is_temporary) ? Number(initial.unit_price) : null,
  );
  // 梯度规则的维度不是数量时（按重量/体积分档）：确定档位用的值，和计费数量是两个独立的输入
  const [tierValue, setTierValue] = useState<number | null>(initial?.tier_value != null ? Number(initial.tier_value) : null);
  const [reason, setReason] = useState(initial?.price_reason || '');
  const [billingUnit, setBillingUnit] = useState<string>(initial?.is_temporary ? initial.billing_unit : 'PER_ITEM');
  const [unitLabel, setUnitLabel] = useState(initial?.is_temporary ? initial.unit_label || '' : '');
  const [currency, setCurrency] = useState<string>(initial?.currency || 'USD');
  const [hint, setHint] = useState<PriceHint | null>(null);
  const [hintError, setHintError] = useState('');
  const [saving, setSaving] = useState(false);
  const timer = useRef<any>(null);
  // 价格查询函数可能每次渲染都是新的，用 ref 取最新的，避免反复触发查询
  const fetchHintRef = useRef(fetchHint);
  fetchHintRef.current = fetchHint;

  const rule = useMemo(() => (rules || []).find(item => item.id === ruleId) || null, [rules, ruleId]);

  // 编辑一条按规则价的行：规则列表加载好后，固定收费规则同样预填规则价（改过价的行保留已改的价格）
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !initial || legacy || initial.is_temporary || !rule) return;
    prefilled.current = true;
    if (initial.price_source === 'override') return;
    if (rule.rule_type === 'flat') setUnitPrice(Number(rule.unit_price));
    else setUnitPrice(tierPriceOf(rule, initial.tier_value != null ? Number(initial.tier_value) : null));
  }, [initial, legacy, rule]);

  const needsTier = !!rule?.needs_tier_value;
  const [basisName, basisUnit] = rule ? [rule.tier_basis_name, rule.tier_basis_unit] : ['', ''];

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setHintError('');
    if (legacy || mode !== 'rule' || !ruleId || !quantity || quantity <= 0 || (needsTier && (tierValue === null || tierValue < 0))) { setHint(null); return undefined; }
    timer.current = setTimeout(() => {
      fetchHintRef.current({ rule_id: ruleId, quantity, ...(needsTier && tierValue !== null ? { tier_value: tierValue } : {}) })
        .then(setHint)
        .catch(err => { setHint(null); setHintError(errorText(err, '查询规则价格失败')); });
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [legacy, mode, ruleId, quantity, needsTier, tierValue]);

  const ruleOptions = useMemo(() => {
    const options = LEVEL_ORDER
      .map(level => ({
        label: NONSTD_RULE_LEVEL_TEXT[level],
        options: (rules || []).filter(item => item.level === level).map(item => ({
          value: item.id,
          disabled: !item.usable,
          label: `${item.label} · ${item.rule_type === 'tiered' ? '梯度计价' : `${item.unit_price} ${item.currency}`}/${plainUnit(item.unit_label) || UNIT_TEXT[item.billing_unit] || ''}${item.usable ? '' : '（不支持，请用临时收费）'}`,
        })),
      }))
      .filter(group => group.options.length > 0);
    // 已经引用的规则后来不再适用（停用/过期/被覆盖）：仍然显示出来，保存时由后端校验
    if (initial?.rule_id && !(rules || []).some(item => item.id === initial.rule_id)) {
      options.push({ label: '已不适用', options: [{ value: initial.rule_id, disabled: false, label: initial.rule_name || `规则 #${initial.rule_id}` }] });
    }
    return options;
  }, [rules, initial]);

  // 固定收费规则：选定后单价直接预填规则价；所有梯度规则都一样：先填“确定单价档位的值”（数量/重量/体积，和计费数量是两个独立的值），
  // 填了就自动填入该档单价，之后可以手改
  const rulePrice = hint?.unit_price !== undefined
    ? Number(hint.unit_price)
    : (rule && rule.rule_type === 'flat' ? Number(rule.unit_price) : tierPriceOf(rule, tierValue));
  // 预估总价 = 单价 × 数量（引用规则时单价留空就按规则价）
  const effectivePrice = unitPrice !== null ? unitPrice : (mode === 'rule' ? rulePrice : null);
  const estimatedTotal = effectivePrice !== null && quantity ? (Math.round(effectivePrice * quantity * 100) / 100).toFixed(2) : null;
  const totalCurrency = mode === 'rule' ? (hint?.currency || rule?.currency || 'USD') : currency;
  const estimate = (
    <span className="nonstd-charge-estimate" style={{ color: '#595959' }}>
      预估总价：<b>{estimatedTotal !== null ? `${estimatedTotal} ${totalCurrency}` : '-'}</b>
    </span>
  );
  // 规则价还没查回来时（例如刚打开编辑）先当作改价处理，保留已填的价格；是否真的改价、原因是否必填由后端最终校验
  const priceChanged = mode === 'rule' && unitPrice !== null && (rulePrice === null || unitPrice !== rulePrice);
  const knownPriceChange = priceChanged && rulePrice !== null;
  const unitText = mode === 'rule' ? (plainUnit(hint?.unit_label) || plainUnit(rule?.unit_label) || UNIT_TEXT[rule?.billing_unit || ''] || '') : (plainUnit(unitLabel) || UNIT_TEXT[billingUnit] || '');

  const submit = async () => {
    if (legacy) {
      setSaving(true);
      try {
        await onSubmit({ description: description.trim() });
      } catch (err: any) {
        message.error(errorText(err, '保存失败'));
      } finally {
        setSaving(false);
      }
      return;
    }
    if (mode === 'rule' && !ruleId) { message.warning('请选择要引用的收费规则'); return; }
    if (mode === 'temporary' && !description.trim()) { message.warning('请填写收费项目名称'); return; }
    if (mode === 'rule' && needsTier && tierValue === null) { message.warning(`这条规则按${basisName}分档，请填写用来确定单价档位的${basisName}`); return; }
    if (!quantity || quantity <= 0) { message.warning('请填写大于 0 的数量'); return; }
    if (mode === 'temporary' && unitPrice === null) { message.warning('请填写单价'); return; }
    if (mode === 'temporary' && !reason.trim()) { message.warning('请说明为什么用临时收费（没有可用的收费规则）'); return; }
    if (knownPriceChange && !reason.trim()) { message.warning('改了规则价格需要说明原因'); return; }
    const payload: NonStdChargeLinePayload = mode === 'rule'
      ? {
        mode, rule_id: ruleId, description: description.trim(), quantity: String(quantity),
        ...(needsTier ? { tier_value: String(tierValue) } : {}),
        unit_price: priceChanged ? String(unitPrice) : null, price_reason: priceChanged ? reason.trim() : '',
      }
      : {
        mode, description: description.trim(), quantity: String(quantity), unit_price: String(unitPrice),
        billing_unit: billingUnit, unit_label: unitLabel.trim(), currency, price_reason: reason.trim(),
      };
    setSaving(true);
    try {
      await onSubmit(payload);
    } catch (err: any) {
      message.error(errorText(err, '保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <Space wrap>
      <Button type="primary" loading={saving} onClick={submit}>{initial ? '保存修改' : (submitText || '添加收费行')}</Button>
      {onCancel ? <Button onClick={onCancel}>取消</Button> : null}
    </Space>
  );

  if (legacy) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <div style={{ fontSize: 12, color: '#8c8c8c' }}>这是旧版本创建的收费行，只能修改说明；要改价格或数量，请作废后重新添加。</div>
        <Input maxLength={200} placeholder="收费说明" value={description} onChange={e => setDescription(e.target.value)} />
        {footer}
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {initial ? null : (
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={mode}
          onChange={e => { setMode(e.target.value); setHint(null); setUnitPrice(null); setReason(''); }}
          options={[{ value: 'rule', label: '选择已有收费规则' }, { value: 'temporary', label: '临时收费' }]}
        />
      )}
      {mode === 'rule' ? (
        <>
          <Space wrap>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 420 }}
              loading={rules === null}
              placeholder={rules === null ? '正在加载收费规则…' : '选择收费规则（项目级 / 客户级 / 系统级）'}
              value={ruleId}
              onChange={value => {
                setRuleId(value);
                const picked = (rules || []).find(item => item.id === value);
                setUnitPrice(picked && picked.rule_type === 'flat' ? Number(picked.unit_price) : null);
                setTierValue(null);
                setReason('');
              }}
              options={ruleOptions}
              notFoundContent="本项目没有可用的收费规则，请改用“临时收费”"
            />
            {needsTier ? (
              <InputNumber
                style={{ width: 190 }}
                min={0}
                placeholder={basisName === '数量' ? '确定单价档位的数量' : `${basisName}${basisUnit ? `（${basisUnit}）` : ''}，确定单价档位`}
                value={tierValue}
                onChange={v => {
                  const value = typeof v === 'number' ? v : null;
                  setTierValue(value);
                  // 填了确定梯度的值：自动填入这个档位的单价（之后仍可手改）；没命中任何档位就清空，由后端提示
                  setUnitPrice(tierPriceOf(rule, value));
                }}
              />
            ) : null}
          </Space>
          <Space wrap>
            <InputNumber
              style={{ width: 170 }}
              min={0}
              precision={2}
              placeholder={rule?.rule_type === 'tiered' ? `单价（按${basisName}匹配档位，留空即按档位价）` : '单价'}
              value={unitPrice}
              onChange={v => setUnitPrice(typeof v === 'number' ? v : null)}
            />
            <InputNumber
              style={{ width: 150 }}
              min={0.000001}
              placeholder={`${needsTier ? '计费数量' : '数量'}${unitText ? `（${unitText}）` : ''}`}
              value={quantity}
              onChange={v => setQuantity(typeof v === 'number' ? v : null)}
            />
            {estimate}
          </Space>
          <Input maxLength={200} placeholder="收费说明（可选，默认用规则名称），如 检测 LPN 商品" value={description} onChange={e => setDescription(e.target.value)} />
          {rule ? (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {rule.level_text}规则：{rule.rule_type === 'tiered'
                ? `梯度计价（按${rule.tier_basis_name || '数量'}${rule.tier_basis_unit ? `（${rule.tier_basis_unit}）` : ''}分档：${rule.tiers.map(t => `${t.min_value ?? 0}${t.max_value ? `~${t.max_value}` : '以上'}：${t.unit_price}`).join('；')}）`
                : `${rule.unit_price} ${rule.currency}/${plainUnit(rule.unit_label) || UNIT_TEXT[rule.billing_unit] || ''}`}
              {rule.remark ? `；${rule.remark}` : ''}
              {rule.visible_in_customer_portal ? '' : '；该规则对客户后台不可见（客户看不到规则名称）'}
            </div>
          ) : null}
          {hint && rulePrice !== null ? (
            <div style={{ fontSize: 12, color: '#389e0d' }}>
              {`${needsTier ? `${basisName}${tierValue}${basisUnit}所在档位` : '本次数量适用'}的规则价：${hint.unit_price} ${hint.currency}/${unitText}`}{unitPrice === null ? '（单价留空即按此价格）' : ''}
            </div>
          ) : null}
          {hint?.warning ? <div style={{ fontSize: 12, color: '#d46b08' }}>{hint.warning}</div> : null}
          {hintError ? <div style={{ fontSize: 12, color: '#cf1322' }}>{hintError}</div> : null}
          {priceChanged ? (
            <Input maxLength={300} placeholder="改价原因（必填，客户可见），如：客户同意的加急价" value={reason} onChange={e => setReason(e.target.value)} />
          ) : null}
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>没有合适的收费规则时用临时收费：只记在这条指令的收费里，不会在收费规则里新增规则。</div>
          <Space wrap>
            <Input style={{ width: 260 }} maxLength={200} placeholder="收费项目名称（必填），如 特殊检测" value={description} onChange={e => setDescription(e.target.value)} />
            <Select style={{ width: 140 }} value={billingUnit} onChange={setBillingUnit} options={NONSTD_BILLING_UNITS} />
            <Input style={{ width: 150 }} maxLength={30} placeholder="展示单位（可选），如 项" value={unitLabel} onChange={e => setUnitLabel(e.target.value)} />
          </Space>
          <Space wrap>
            <InputNumber
              style={{ width: 150 }}
              min={0.000001}
              placeholder={`数量${unitText ? `（${unitText}）` : ''}`}
              value={quantity}
              onChange={v => setQuantity(typeof v === 'number' ? v : null)}
            />
            <InputNumber style={{ width: 150 }} min={0} precision={2} placeholder="单价（必填）" value={unitPrice} onChange={v => setUnitPrice(typeof v === 'number' ? v : null)} />
            <Select
              style={{ width: 100 }}
              value={currency}
              onChange={setCurrency}
              options={[{ value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' }, { value: 'EUR', label: 'EUR' }]}
            />
            {estimate}
          </Space>
          <Input maxLength={300} placeholder="原因（必填，客户可见）：为什么没有可用的收费规则，如 一次性报价" value={reason} onChange={e => setReason(e.target.value)} />
        </>
      )}
      {footer}
    </Space>
  );
};
